"use client";

import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FigmaProductCard } from './FigmaProductCard';

export const FigmaProductCarousel = ({ products = [], limit = 6 }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const visibleProducts = Array.isArray(products) ? products.slice(0, limit) : [];

  const checkScroll = () => {
    const node = scrollRef.current;
    if (!node) return;

    const { scrollLeft, scrollWidth, clientWidth } = node;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [visibleProducts.length]);

  const scroll = (direction) => {
    const node = scrollRef.current;
    if (!node) return;

    const amount = direction === 'left' ? -node.clientWidth + 140 : node.clientWidth - 140;
    node.scrollBy({ left: amount, behavior: 'smooth' });
    window.setTimeout(checkScroll, 350);
  };

  if (!visibleProducts.length) return null;

  return (
    <div className="relative w-full group/slider">
      <button
        type="button"
        onClick={() => scroll('left')}
        disabled={!canScrollLeft}
        className="absolute left-0 top-1/2 z-10 hidden h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-lg opacity-0 transition-all duration-300 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-0 md:flex md:group-hover/slider:opacity-100"
        aria-label="Scroll left"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      <button
        type="button"
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
        className="absolute right-0 top-1/2 z-10 hidden h-11 w-11 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-lg opacity-0 transition-all duration-300 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-0 md:flex md:group-hover/slider:opacity-100"
        aria-label="Scroll right"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-4 overflow-x-auto pb-2 pr-2 pt-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {visibleProducts.map((product) => (
          <div key={product._id || product.id || product.slug} className="w-[192px] shrink-0 sm:w-[198px] lg:w-[206px]">
            <FigmaProductCard product={product} />
          </div>
        ))}
      </div>
    </div>
  );
};
